import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { faArrowLeft, faPlus, faBookmark } from '@fortawesome/pro-light-svg-icons';
import { ReservasService } from 'src/app/services/reservas.service';
import { DialogGenericComponent } from 'src/app/components/dialog-generic/dialog-generic.component';

/**
 * ReservasListComponent - Listado de reservas existentes.
 * Permite crear nuevas reservas y navegar al detalle de cada una.
 */
@Component({
  selector: 'app-reservas-list',
  templateUrl: './reservas-list.component.html',
  styleUrls: ['./reservas-list.component.scss'],
})
export class ReservasListComponent {
  faArrowLeft = faArrowLeft;
  faPlus = faPlus;
  faBookmark = faBookmark;

  reservations: any[] = [];
  loading: boolean = true;

  // Paginación (la API devuelve 15 por página)
  currentPage: number = 1;
  lastPage: number = 1;
  totalReservations: number = 0;
  loadingMore: boolean = false;
  private requestId: number = 0;

  searchName: string = '';
  private searchTimeout: any = null;

  filterStatus: string = '';
  filterEventIds: number[] = [];
  availableEvents: { id: number; title: string }[] = [];
  eventDropdownOpen: boolean = false;
  eventSearch: string = '';

  get filteredAvailableEvents(): { id: number; title: string }[] {
    if (!this.eventSearch.trim()) return this.availableEvents;
    const q = this.eventSearch.toLowerCase();
    return this.availableEvents.filter(e => e.title.toLowerCase().includes(q));
  }
  statusDropdownOpen: boolean = false;

  readonly statusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'reserved', label: 'Reservada' },
    { value: 'completed', label: 'Completada' },
  ];

  // Formulario nueva reserva
  showNewForm: boolean = false;
  newReservationName: string = '';
  savingReservation: boolean = false;

  constructor(
    private reservasService: ReservasService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadReservations();
  }

  /** Carga la primera página; `loadMore()` añade las siguientes */
  loadReservations(page: number = 1): void {
    if (page === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    const filters = {
      name: this.searchName,
      status: this.filterStatus,
      event_ids: this.filterEventIds,
    };
    // Descarta respuestas de una petición anterior (p. ej. "cargar más" seguido de un cambio de filtro)
    const requestId = ++this.requestId;
    this.reservasService.getReservations(filters, page).subscribe({
      next: (res) => {
        if (requestId !== this.requestId) return;
        const items = res.data || res || [];
        this.reservations = page === 1 ? items : [...this.reservations, ...items];
        this.currentPage = res.current_page || page;
        this.lastPage = res.last_page || 1;
        this.totalReservations = res.total ?? this.reservations.length;
        if (!filters.status && !filters.event_ids?.length && !filters.name) {
          this.buildAvailableEvents();
        }
        this.loading = false;
        this.loadingMore = false;
      },
      error: () => {
        if (requestId !== this.requestId) return;
        this.loading = false;
        this.loadingMore = false;
        this.showError('No se han podido cargar las reservas.');
      },
    });
  }

  get hasMorePages(): boolean {
    return this.currentPage < this.lastPage;
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMorePages) return;
    this.loadReservations(this.currentPage + 1);
  }

  onSearchChange(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.loadReservations(), 400);
  }

  onFilterChange(): void {
    this.loadReservations();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterEventIds = [];
    this.statusDropdownOpen = false;
    this.eventDropdownOpen = false;
    this.loadReservations();
  }

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  closeStatusDropdown(): void {
    this.statusDropdownOpen = false;
  }

  selectStatus(value: string): void {
    this.filterStatus = value;
    this.statusDropdownOpen = false;
    this.onFilterChange();
  }

  get statusFilterLabel(): string {
    return this.statusOptions.find(o => o.value === this.filterStatus)?.label || 'Todos los estados';
  }

  toggleEventDropdown(): void {
    this.eventDropdownOpen = !this.eventDropdownOpen;
  }

  closeEventDropdown(): void {
    this.eventDropdownOpen = false;
    this.eventSearch = '';
  }

  toggleEventFilter(eventId: number): void {
    const idx = this.filterEventIds.indexOf(eventId);
    if (idx === -1) {
      this.filterEventIds = [...this.filterEventIds, eventId];
    } else {
      this.filterEventIds = this.filterEventIds.filter(id => id !== eventId);
    }
    this.onFilterChange();
  }

  get eventFilterLabel(): string {
    if (!this.filterEventIds.length) return 'Todos los eventos';
    if (this.filterEventIds.length === 1) {
      return this.availableEvents.find(e => e.id === this.filterEventIds[0])?.title || '1 evento';
    }
    return `${this.filterEventIds.length} eventos`;
  }


  openNewForm(): void {
    this.newReservationName = '';
    this.showNewForm = true;
  }

  cancelNewForm(): void {
    this.showNewForm = false;
  }

  /** Crea la reserva y redirige al detalle para añadir entradas */
  createReservation(): void {
    if (!this.newReservationName?.trim()) return;
    this.savingReservation = true;
    this.reservasService
      .createReservation(this.newReservationName.trim())
      .subscribe({
        next: (res) => {
          const reservation = res.data || res;
          this.savingReservation = false;
          this.showNewForm = false;
          // Redirigir al detalle para añadir entradas inmediatamente
          this.router.navigate(['/reservas', reservation.id]);
        },
        error: () => {
          this.savingReservation = false;
          this.showError('No se ha podido crear la reserva.');
        },
      });
  }

  goToDetail(id: number): void {
    this.router.navigate(['/reservas', id]);
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  getReservationTotal(res: any): number {
    const lines = res.cart?.cart_lines || [];
    return lines.reduce((acc: number, l: any) => acc + (+l.price - +l.fee), 0);
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      reserved: 'Reservada',
      pending: 'Pendiente',
      active: 'Activa',
      processing: 'Procesando pago',
      paid: 'Pagada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      deleted: 'Eliminada',
    };
    return labels[status] ?? status ?? 'Pendiente';
  }

  private buildAvailableEvents(): void {
    const eventsMap = new Map<number, string>();
    this.reservations.forEach(res => {
      res.cart?.cart_lines?.forEach((line: any) => {
        if (line.event?.id && line.event?.title) {
          eventsMap.set(line.event.id, line.event.title);
        }
      });
    });
    this.availableEvents = Array.from(eventsMap.entries())
      .map(([id, title]) => ({ id, title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  private showError(message: string): void {
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Error',
        subtitle: message,
        icon: 'error',
        buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
      },
    });
  }
}
